import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, MapPin, Package, Flower2, Sparkles, Camera, LogOut, CalendarHeart, Wallet, History, Images, Tag, ClipboardList } from "lucide-react";
import api from "@/lib/api";
import { eur, fmtDate, Loading, StatusBadge, Stat, EmptyState } from "@/components/common";
import Section from "@/components/Section";
import OffertaCura from "@/components/OffertaCura";
import CampaignBanner from "@/components/CampaignBanner";
import { useAuth } from "@/context/AuthContext";

const DURATION_LABEL = { 1: "1 mese", 3: "3 mesi", 6: "6 mesi", 12: "12 mesi" };
const SUB_STATUS = { attiva: "Attivo", in_attesa_attivazione: "In attesa di attivazione", sospesa: "Sospeso", terminata: "Terminato", annullata: "Annullato" };
const NAV_ITEMS = [
  ["client-home", "Home"],
  ["client-luoghi", "I miei luoghi"],
  ["client-interventi", "Interventi"],
  ["client-abbonamenti", "Abbonamenti"],
  ["client-profilo", "Profilo"],
];
const NAV_TARGET = {
  "client-home": [null, "client-home"],
  "client-luoghi": ["luoghi", "client-luoghi"],
  "client-interventi": ["interventi", "client-interventi"],
  "client-abbonamenti": ["subs", "client-abbonamenti"],
  "client-profilo": [null, "client-profilo"],
};

export default function AreaCliente() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [graves, setGraves] = useState([]);
  const [credits, setCredits] = useState(null);
  const [payments, setPayments] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [compleanni, setCompleanni] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/customer/dashboard").then((r) => setData(r.data));
    api.get("/customer/graves").then((r) => setGraves(r.data));
    api.get("/customer/credits").then((r) => setCredits(r.data));
    api.get("/customer/payments").then((r) => setPayments(r.data));
    api.get("/customer/interventions").then((r) => setInterventions(r.data));
    api.get("/customer/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    api.get("/customer/subscriptions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/customer/compleanni-reminder").then((r) => setCompleanni(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  if (!data || !credits) return <Loading />;

  const unread = notifs.filter((n) => !n.read).length;
  const unreadRefIds = new Set(notifs.filter((n) => !n.read && n.ref_id).map((n) => n.ref_id));
  const graveOf = (gid) => graves.find((g) => g.id === gid);
  const nextFor = (sid) =>
    data.upcoming
      .filter((i) => i.subscription_id === sid && i.scheduled_date)
      .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))[0];

  const isOpen = (key, fallback = false) => (key in open ? open[key] : fallback);
  const openAndScroll = (key, domId) => {
    if (key) setOpen((o) => ({ ...o, [key]: true }));
    setTimeout(() => document.getElementById(domId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };
  const navGo = (id) => {
    const [key, domId] = NAV_TARGET[id];
    openAndScroll(key, domId);
  };

  const openNotification = async (n) => {
    setBellOpen(false);
    if (!n.read) {
      try {
        await api.post(`/customer/notifications/${n.id}/read`);
        setNotifs((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch {
        toast.error("Non riesco a segnare la notifica come letta.");
      }
    }
    if (n.ref_id) {
      navigate(n.request_type === "nuova_tomba" ? `/area-cliente/tombe/${n.ref_id}` : `/area-cliente/interventi/${n.ref_id}`);
    }
  };

  const markAllRead = async () => {
    await api.post("/customer/notifications/read-all").catch(() => {});
    setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const sortedSubs = [...subs].sort((a, b) => (a.status === "attiva" ? -1 : 1) - (b.status === "attiva" ? -1 : 1));
  const completed = interventions
    .filter((i) => i.status === "completata")
    .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));
  const nextIntervention = data.upcoming
    .filter((i) => i.scheduled_date)
    .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))[0];
  const visiblePlans = plans.filter((p) => !(p.duration_months === 3 && p.frequency === 2));
  const hasActiveSub = sortedSubs.some((s) => s.status === "attiva");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="client-dashboard">
      <div id="client-home" className="flex items-center justify-between gap-4 flex-wrap scroll-mt-24">
        <div>
          <p className="eyebrow mb-1">Area riservata</p>
          <h1 className="font-serif text-4xl text-navy" data-testid="client-greeting">Ciao, {user?.name}.</h1>
        </div>
        <div className="relative">
          <button
            data-testid="client-bell"
            onClick={() => setBellOpen(!bellOpen)}
            className="relative p-3 rounded-full border border-[#e2e8f0] bg-white text-navy hover:bg-[#f0f7ff] transition-colors duration-200"
            aria-label="Notifiche"
          >
            <Bell size={20} />
            {unread > 0 && (
              <span data-testid="client-bell-badge" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#4a7c59] text-white text-[11px] font-bold flex items-center justify-center shadow-sm">
                {unread}
              </span>
            )}
          </button>
          {bellOpen && (
            <div className="absolute right-0 mt-2 w-80 max-w-[85vw] card-soft p-2 z-30 space-y-1" data-testid="client-bell-panel">
              {notifs.length === 0 ? (
                <p className="text-sm text-muted-soft p-3">Nessuna notifica.</p>
              ) : (
                <>
                  {unread > 0 && (
                    <button data-testid="client-read-all" onClick={markAllRead} className="text-xs font-semibold text-[#1e3a5f] hover:underline px-3 pt-2">
                      Segna tutte come lette
                    </button>
                  )}
                  {notifs.slice(0, 8).map((n) => (
                    <button
                      key={n.id}
                      data-testid={`client-notif-${n.id}`}
                      onClick={() => openNotification(n)}
                      className={`w-full text-left p-3 rounded-xl text-sm transition-colors duration-200 hover:bg-[#f0f7ff] ${
                        n.read ? "text-muted-soft" : "text-navy bg-[#f4faf6] border-l-4 border-[#4a7c59]"
                      }`}
                    >
                      <p>{n.message || n.request_type}</p>
                      <p className="text-xs text-muted-soft mt-0.5">{fmtDate(n.date)}</p>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <nav className="flex gap-2 overflow-x-auto pb-1" data-testid="client-nav">
        {NAV_ITEMS.map(([id, label]) => (
          <button
            key={id}
            data-testid={`client-nav-${id}`}
            onClick={() => navGo(id)}
            className="px-4 py-2 min-h-[44px] rounded-full text-sm font-semibold bg-white border border-[#dbe7f3] text-[#1e3a5f] whitespace-nowrap hover:bg-[#f0f7ff] transition-colors duration-200"
          >
            {label}
          </button>
        ))}
      </nav>

      <CampaignBanner testid="campaign-banner-cliente" />

      {compleanni.map((c) => (
        <div key={c.grave_id} className="card-ivory p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`compleanno-reminder-${c.grave_id}`}>
          <div>
            <p className="eyebrow mb-1">Un ricordo che ritorna</p>
            <p className="text-sm text-body leading-relaxed">
              Il compleanno di <span className="font-bold text-navy">{c.deceased_name} {c.deceased_surname}</span> si avvicina
              ({fmtDate(c.date)}{c.days_left === 0 ? ", è proprio oggi" : `, tra ${c.days_left} ${c.days_left === 1 ? "giorno" : "giorni"}`}).
              Vuoi ripetere il pensiero con la composizione in ciotola?
            </p>
          </div>
          <Link to="/abbonamenti" data-testid={`compleanno-reminder-cta-${c.grave_id}`} className="btn-primary whitespace-nowrap self-start sm:self-auto">
            Ripeti il pensiero
          </Link>
        </div>
      ))}

      {nextIntervention && (
        <Link to={`/area-cliente/interventi/${nextIntervention.id}`} data-testid="next-intervention-card"
          className="card-soft p-5 flex items-center justify-between gap-4 flex-wrap hover:shadow-md transition-shadow duration-200">
          <div>
            <p className="eyebrow mb-1">Prossimo intervento</p>
            <p className="font-semibold text-navy">{nextIntervention.service}{nextIntervention.flower_name ? ` · ${nextIntervention.flower_name}` : ""}</p>
            <p className="text-sm text-muted-soft">
              {nextIntervention.grave ? `${nextIntervention.grave.deceased_name} ${nextIntervention.grave.deceased_surname}` : ""} · {nextIntervention.cemetery?.name} · {fmtDate(nextIntervention.scheduled_date)}
            </p>
          </div>
          <StatusBadge status={nextIntervention.status} />
        </Link>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-credits" label={credits.bonus?.source === "offerta" ? "Bonus offerta stagionale" : "Bonus prima uscita"}
          value={credits.bonus?.has_bonus ? eur(credits.bonus.amount) : "—"}
          hint={credits.bonus?.status === "disponibile" ? `Utilizzabile: ${credits.bonus.days_left} giorni rimasti` : credits.bonus?.status === "utilizzato" ? "Bonus già utilizzato" : credits.bonus?.status === "scaduto" ? "Bonus scaduto" : "Nessun bonus attivo"} />
        <Stat testid="stat-subs" label="Abbonamenti attivi" value={data.subscriptions.length} />
        <Stat testid="stat-upcoming" label="Prossimi interventi" value={data.upcoming.length} />
        <Stat testid="stat-completed" label="Interventi completati" value={data.completed_count} />
      </div>

      {credits.bonus?.status === "disponibile" && (
        <div className="card-ivory p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="promo-countdown-banner">
          <div>
            <p className="eyebrow mb-1">{credits.bonus?.source === "offerta" ? "Bonus offerta stagionale" : "Bonus prima uscita"}</p>
            <p className="text-sm text-body leading-relaxed">
              Ti restano <span className="font-bold text-navy">{credits.bonus.days_left} {credits.bonus.days_left === 1 ? "giorno" : "giorni"}</span> per
              usare il bonus di <span className="font-bold text-navy">{eur(credits.bonus.amount)}</span>:
              utilizzabile una sola volta con un abbonamento di almeno 5 interventi.
            </p>
          </div>
          <Link to="/abbonamenti" data-testid="promo-countdown-cta" className="btn-primary whitespace-nowrap self-start sm:self-auto">
            Scopri gli abbonamenti
          </Link>
        </div>
      )}

      <section data-testid="client-actions">
        <h2 className="font-serif text-2xl text-navy mb-4">Cosa vuoi fare?</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/area-cliente/tombe/nuova" data-testid="new-grave-btn" className="card-soft p-5 min-h-[44px] flex flex-col items-start gap-3 hover:shadow-md transition-shadow duration-200">
            <MapPin size={22} className="text-[#4a7c59]" />
            <span className="font-semibold text-navy text-sm">Registra un altro luogo</span>
          </Link>
          <button onClick={() => openAndScroll("subs", "client-abbonamenti")} data-testid="action-abbonamenti" className="card-soft p-5 min-h-[44px] flex flex-col items-start gap-3 text-left hover:shadow-md transition-shadow duration-200">
            <Package size={22} className="text-[#4a7c59]" />
            <span className="font-semibold text-navy text-sm">I miei abbonamenti</span>
          </button>
          <Link to="/singola-uscita" data-testid="action-uscita-singola" className="card-soft p-5 min-h-[44px] flex flex-col items-start gap-3 hover:shadow-md transition-shadow duration-200">
            <Flower2 size={22} className="text-[#4a7c59]" />
            <span className="font-semibold text-navy text-sm">Acquista un'uscita singola</span>
          </Link>
          <Link to="/servizi/pulizia" data-testid="action-pulizia" className="card-soft p-5 min-h-[44px] flex flex-col items-start gap-3 hover:shadow-md transition-shadow duration-200">
            <Sparkles size={22} className="text-[#4a7c59]" />
            <span className="font-semibold text-navy text-sm">Pulizia</span>
          </Link>
        </div>
      </section>

      <Section title="I miei abbonamenti" count={sortedSubs.length} icon={Package} testid="my-subscriptions" id="client-abbonamenti"
        open={isOpen("subs", false)} onToggle={(v) => setOpen((o) => ({ ...o, subs: v }))}>
        {sortedSubs.length === 0 ? (
          <EmptyState title="Nessun abbonamento attivo">
            <Link to="/abbonamenti" className="text-[#4a7c59] font-semibold underline">Scopri gli abbonamenti</Link>
          </EmptyState>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {sortedSubs.map((s) => {
              const g = graveOf(s.grave_id);
              const next = nextFor(s.id);
              const remaining = Math.max(0, (s.interventions_total || 0) - (s.interventions_used || 0));
              return (
                <div key={s.id} className="card-soft p-6" data-testid={`sub-card-${s.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <p className="font-semibold text-navy">{s.title}</p>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${s.status === "attiva" ? "text-[#2f5d3e] bg-[#e8f0eb]" : s.status === "in_attesa_attivazione" ? "text-amber-900 bg-amber-100" : "text-muted-soft bg-[#f1f5f9]"}`}>
                      {SUB_STATUS[s.status] || s.status}
                    </span>
                  </div>
                  <p className="text-sm text-body mt-1 flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#4a7c59] shrink-0" />
                    {g ? `${g.cemetery?.name || ""} · ${g.deceased_name} ${g.deceased_surname}` : "Luogo associato"}
                  </p>
                  <div className="text-sm text-body mt-3 space-y-1">
                    <p>Inizio: {s.start_date ? fmtDate(s.start_date) : "—"} · Scadenza: {s.end_date ? fmtDate(s.end_date) : "—"}</p>
                    <p data-testid={`sub-progress-${s.id}`}>
                      {s.interventions_total} interventi previsti · {s.interventions_used} effettuati · {remaining} rimanenti
                    </p>
                    <p className="text-[#1e3a5f] font-medium">
                      Prossimo intervento: {next ? fmtDate(next.scheduled_date) : "da programmare"}
                    </p>
                  </div>
                  <p className="text-xs text-muted-soft mt-2">
                    Pagato {eur(s.amount_paid)}{s.credit_applied > 0 ? ` (bonus applicato: −${eur(s.credit_applied)})` : ""}
                  </p>
                  {s.extras?.length > 0 && (
                    <p className="text-xs text-[#4a7c59] font-semibold mt-1" data-testid={`sub-extras-${s.id}`}>
                      + {s.extras.length} ricorrenze: {s.extras.map((e) => e.name).join(", ")} ({eur(s.extras_total)})
                    </p>
                  )}
                  <button onClick={() => openAndScroll("interventi", "client-interventi")} data-testid={`sub-view-${s.id}`} className="btn-secondary mt-4 !py-2 text-sm w-full min-h-[44px]">
                    Vedi abbonamento
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <div id="client-interventi" data-testid="client-interventi" className="space-y-5 scroll-mt-24">
        <Section title="Prossimi interventi" count={data.upcoming.length} icon={CalendarHeart} testid="upcoming-interventions"
          open={isOpen("interventi", false)} onToggle={(v) => setOpen((o) => ({ ...o, interventi: v }))}>
          {data.upcoming.length === 0 ? (
            <EmptyState title="Nessun intervento in programma" />
          ) : (
            <div className="space-y-3">
              {data.upcoming.map((i) => (
                <Link key={i.id} to={`/area-cliente/interventi/${i.id}`} data-testid={`intervention-row-${i.id}`}
                  className="card-soft p-5 min-h-[44px] flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <p className="font-semibold text-navy">{i.service}{i.flower_name ? ` · ${i.flower_name}` : ""}</p>
                    <p className="text-sm text-muted-soft">
                      {i.grave ? `${i.grave.deceased_name} ${i.grave.deceased_surname}` : ""} · {i.cemetery?.name}
                      {i.scheduled_date ? ` · ${fmtDate(i.scheduled_date)}` : " · Da programmare"}
                    </p>
                  </div>
                  <StatusBadge status={i.status} />
                </Link>
              ))}
            </div>
          )}
        </Section>

        <Section title="Ultimi interventi" count={completed.length} icon={History} testid="storico-interventi"
          open={isOpen("storico", false)} onToggle={(v) => setOpen((o) => ({ ...o, storico: v }))}>
          {completed.length === 0 ? (
            <EmptyState title="Nessuna uscita completata" />
          ) : (
            <div className="space-y-3">
              {completed.map((i) => (
                <Link key={i.id} to={`/area-cliente/interventi/${i.id}`} data-testid={`storico-item-${i.id}`}
                  className="card-soft p-5 min-h-[44px] flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    {i.final_photo && <img src={i.final_photo} alt="Foto intervento" className="w-14 h-14 rounded-xl object-cover" />}
                    <div>
                      <p className="font-semibold text-navy flex items-center gap-2">
                        {i.exit_title || `Uscita del: ${fmtDate(i.completed_at)}`}
                        {unreadRefIds.has(i.id) && (
                          <span data-testid={`storico-new-${i.id}`} className="text-[10px] font-bold tracking-widest uppercase text-white bg-[#4a7c59] rounded-full px-2 py-0.5">Nuovo</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-soft">{i.service} · {i.cemetery?.name}</p>
                    </div>
                  </div>
                  <StatusBadge status={i.status} />
                </Link>
              ))}
            </div>
          )}
        </Section>
      </div>

      <Section title="I miei luoghi" count={graves.length} icon={MapPin} testid="my-graves" id="client-luoghi"
        open={isOpen("luoghi", graves.length === 0)} onToggle={(v) => setOpen((o) => ({ ...o, luoghi: v }))}>
        {graves.length === 0 ? (
          <EmptyState title="Nessun luogo registrato">
            Registra il primo luogo nel cimitero per poter richiedere un intervento.
          </EmptyState>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {graves.map((g) => (
              <Link key={g.id} to={`/area-cliente/tombe/${g.id}`} data-testid={`grave-card-${g.id}`} className="card-soft overflow-hidden block">
                {g.photo ? (
                  <img src={g.photo} alt={`Luogo di ${g.deceased_name}`} className="w-full aspect-[16/9] object-cover" />
                ) : (
                  <div className="w-full aspect-[16/9] bg-[#f0f7ff] flex items-center justify-center text-muted-soft text-sm">Nessuna foto</div>
                )}
                <div className="p-5">
                  <p className="font-serif text-xl text-navy">{g.deceased_name} {g.deceased_surname}</p>
                  <p className="text-sm text-muted-soft">{g.cemetery?.name} · Campo {g.campo || "—"}, fila {g.fila || "—"}, n. {g.numero || "—"}</p>
                  <p className="text-xs text-[#4a7c59] font-semibold mt-2">{g.interventions_count} interventi · {g.position_confirmed ? "Posizione confermata" : "Posizione da confermare"}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Offerta in evidenza" icon={Tag} testid="client-offerta" defaultOpen={!hasActiveSub}>
        <OffertaCura testid="client-offer-promo" />
      </Section>

      <Section title="Scegli la tua formula" count={visiblePlans.length} icon={ClipboardList} testid="client-plans" defaultOpen={!hasActiveSub}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {visiblePlans.map((p) => (
            <div key={p.id} className="card-soft p-5 flex flex-col" data-testid={`client-plan-${p.id}`}>
              <p className="font-semibold text-navy">{DURATION_LABEL[p.duration_months] || `${p.duration_months} mesi`}</p>
              <p className="text-sm text-body mt-0.5">
                {p.frequency} intervent{p.frequency === 1 ? "o" : "i"} al mese · {p.interventions_total} interventi totali
                {p.duration_months === 12 && p.frequency === 4 ? " · settimanale" : ""}
              </p>
              <p className="font-serif text-3xl text-navy mt-3 mb-4">{eur(p.price)}</p>
              <Link to={`/checkout?type=abbonamento&plan=${p.id}`} data-testid={`client-plan-cta-${p.id}`} className="btn-secondary mt-auto !py-2 text-sm min-h-[44px]">
                Scegli
              </Link>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Fotografie" count={data.recent_photos.length} icon={Images} testid="photos-section"
        open={isOpen("foto", false)} onToggle={(v) => setOpen((o) => ({ ...o, foto: v }))}>
        {data.recent_photos.length === 0 ? (
          <EmptyState title="Ancora nessuna fotografia">
            Quando un intervento sarà completato, troverai qui la foto finale.
          </EmptyState>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
            {data.recent_photos.map((i) => (
              <Link key={i.id} to={`/area-cliente/interventi/${i.id}`} data-testid={`photo-${i.id}`} className="card-soft overflow-hidden block">
                <img src={i.final_photo} alt="Fotografia intervento" className="w-full aspect-square object-cover" />
                <p className="p-3 text-xs text-muted-soft">{fmtDate(i.completed_at)} · {i.service}</p>
              </Link>
            ))}
          </div>
        )}
      </Section>

      <Section title="Bonus e storico pagamenti" icon={Wallet} testid="history-section"
        open={isOpen("pagamenti", false)} onToggle={(v) => setOpen((o) => ({ ...o, pagamenti: v }))}>
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <h3 className="font-serif text-lg text-navy mb-3">{credits.bonus?.source === "offerta" ? "Bonus offerta stagionale" : "Bonus prima uscita"}</h3>
            <div className="card-soft p-6 space-y-3">
              {!credits.bonus?.has_bonus ? (
                <p className="text-sm text-muted-soft">Nessun bonus: lo ricevi acquistando la Prima Uscita di Prova o un'offerta stagionale (es. il crisantemo di Tutti i Santi).</p>
              ) : (
                <div data-testid="bonus-status" className="text-sm space-y-2">
                  <div className="flex justify-between items-center gap-3">
                    <p className="text-navy font-medium">Bonus di {eur(credits.bonus.amount)}</p>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${credits.bonus.status === "disponibile" ? "text-[#2f5d3e] bg-[#e8f0eb]" : credits.bonus.status === "utilizzato" ? "text-[#1e3a5f] bg-[#f0f7ff]" : "text-muted-soft bg-[#f1f5f9]"}`}>
                      {credits.bonus.status === "disponibile" ? "Disponibile" : credits.bonus.status === "utilizzato" ? "Bonus già utilizzato" : "Bonus scaduto"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-soft leading-relaxed">
                    {credits.bonus.status === "disponibile"
                      ? `Utilizzabile entro il ${fmtDate(credits.bonus.expires_at)} (${credits.bonus.days_left} ${credits.bonus.days_left === 1 ? "giorno" : "giorni"} rimasti). Una sola volta, con un abbonamento di almeno 5 interventi.`
                      : credits.bonus.status === "utilizzato"
                        ? `Utilizzato il ${fmtDate(credits.bonus.used_at)} per: ${credits.bonus.used_for}.`
                        : "La finestra di 10 giorni è terminata: il bonus non è più utilizzabile."}
                  </p>
                </div>
              )}
            </div>
          </div>
          <div>
            <h3 className="font-serif text-lg text-navy mb-3">Storico pagamenti</h3>
            <div className="card-soft p-6 space-y-3">
              {payments.length === 0 ? (
                <p className="text-sm text-muted-soft">Nessun pagamento.</p>
              ) : (
                payments.map((p) => (
                  <div key={p.id} data-testid={`payment-${p.id}`} className="flex justify-between text-sm border-b border-[#f1f5f9] pb-2 last:border-0">
                    <div>
                      <p className="text-navy font-medium capitalize">{p.kind} · {p.method}</p>
                      <p className="text-xs text-muted-soft">{fmtDate(p.created_at)} · {p.status === "completato" ? "Completato" : p.status === "in_attesa" ? "In attesa" : "Preventivo"}</p>
                    </div>
                    <span className="font-semibold text-navy">{eur(p.amount)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </Section>

      <section id="client-profilo" data-testid="client-profilo" className="scroll-mt-24">
        <h2 className="font-serif text-2xl text-navy mb-4">Profilo</h2>
        <div className="card-soft p-6 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-navy">{user?.name} {user?.surname}</p>
            <p className="text-sm text-muted-soft">{user?.email}</p>
          </div>
          <button
            data-testid="client-logout"
            onClick={() => { logout(); window.location.href = "/"; }}
            className="btn-secondary !py-2 text-sm min-h-[44px] inline-flex items-center gap-2"
          >
            <LogOut size={16} /> Esci
          </button>
        </div>
      </section>
    </div>
  );
}
